import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { hotTopics } from '@/mocks/hotTopics';
import NotFound from '@/pages/NotFound';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articles.filter((a) => a.category === category.id);
  const featuredInCategory = categoryArticles.find((a) => a.featured) || categoryArticles[0];
  const otherArticles = categoryArticles.filter((a) => a.id !== featuredInCategory?.id);

  const hotInCategory = hotTopics.slice(0, 4);

  const formatHotValue = (val: number) => {
    if (val >= 10000) return `${(val / 10000).toFixed(1)}万`;
    return val.toLocaleString();
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 吃瓜爆料`,
    description: category.description,
    url: `http://94exue.cn/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜爆料',
      url: 'http://94exue.cn/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'http://94exue.cn/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: category.name,
          item: `http://94exue.cn/category/${category.slug}`,
        },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 吃瓜爆料`}
        description={category.description}
        canonical={`http://94exue.cn/category/${category.slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20night%20scene%20of%20crowded%20urban%20street%20with%20neon%20signs%20and%20mysterious%20atmospheric%20lighting%2C%20deep%20emerald%20green%20and%20warm%20amber%20color%20palette%2C%20paparazzi%20style%20energy%2C%20abstract%20artistic%20interpretation%20of%20gossip%20and%20secrets%20unfolding%2C%20editorial%20photography%20with%20strong%20contrast%20and%20moody%20atmosphere&width=1920&height=800&seq=cat-hero&orientation=landscape"
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-12 lg:px-20 pt-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-4">
                {category.name}
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-lg leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>

          <div className="absolute bottom-6 left-0 right-0 text-center z-10">
            <p className="text-5xl md:text-7xl font-heading font-black text-white/15 tracking-tight">
              吃瓜爆料
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: category.name }]} />
          </div>
        </div>

        {/* Category Switcher Tabs */}
        <div className="bg-background-50 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <span className="text-xs text-foreground-400 mr-2 flex-shrink-0">切换分类：</span>
              {categories.map((cat) => {
                const isActive = cat.slug === slug;
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`} />
                    {cat.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Featured Article + Hot Topics */}
        <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Featured */}
              {featuredInCategory && (
                <Link
                  to={`/article/${featuredInCategory.id}`}
                  className="lg:col-span-2 group block rounded-xl overflow-hidden bg-foreground-800 transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={featuredInCategory.image}
                      alt={featuredInCategory.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1.5 bg-primary-500 text-white text-xs font-bold rounded-full">
                        置顶爆料
                      </span>
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-white mb-3 leading-snug">
                      {featuredInCategory.title}
                    </h2>
                    <p className="text-sm text-white/70 mb-4 leading-relaxed">
                      {featuredInCategory.summary}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-white/50">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line" />
                        {featuredInCategory.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {featuredInCategory.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line text-primary-400" />
                        {formatHotValue(featuredInCategory.hotValue)}
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Hot Topics Sidebar */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground-800 mb-4">
                  该分类热门
                </h3>
                {hotInCategory.map((topic, index) => (
                  <Link
                    key={topic.id}
                    to={`/article/${topic.id.replace('hot', 'article')}`}
                    className="flex items-center gap-3 group"
                  >
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                        index < 3 ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {topic.title}
                      </p>
                      <p className="text-xs text-foreground-400 mt-0.5">
                        {formatHotValue(topic.hotValue)} 热度
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-6 md:mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {category.name}文章
              </h2>
              <span className="text-xs text-foreground-400">
                共 {categoryArticles.length} 篇
              </span>
            </div>

            {otherArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {otherArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all hover:-translate-y-1"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-primary-500" />
                          {formatHotValue(article.hotValue)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-foreground-500">该分类暂无更多文章</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative min-h-[400px] md:min-h-[480px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20wide%20panoramic%20scene%20of%20giant%20watermelon%20field%20under%20dramatic%20sunset%20sky%20with%20golden%20hour%20lighting%2C%20warm%20amber%20and%20deep%20green%20color%20palette%2C%20surreal%20dreamlike%20atmosphere%2C%20editorial%20landscape%20photography%20with%20strong%20visual%20impact%20and%20moody%20cinematic%20tone&width=1920&height=800&seq=cat-cta&orientation=landscape"
              alt="订阅吃瓜爆料"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-12 lg:px-20">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <h2 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight mb-4">
                  不错过任何精彩
                </h2>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-white text-foreground-900 rounded-full text-sm font-semibold hover:bg-white/90 transition-all whitespace-nowrap"
                >
                  立即订阅
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-foreground-900 text-white">
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </Link>
              </div>
              <p className="text-sm text-white/80 max-w-sm leading-relaxed lg:text-right">
                订阅吃瓜爆料，第一时间获取{category.name}最新动态推送。精彩内容每日更新，劲爆内容不打烊。
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
